import { TableCell, TableRow } from "@mui/material";
import { styled } from "@mui/material/styles";
import * as React from "react";
import { fetchData } from "../../../common/services/ApiClient";
import { ApiBaseUrl } from "../../../config";
import { KeyValue, Query } from "../../../interfaces";

import generateBranchOfficeDepositsApiUrl, {
  combineStateData,
  filterDataForRow,
} from "./BranchOfficeDepositsUtilities";
import { chunkArray } from "../../../common/services/Utilities";

const PREFIX = "CountyInstType";

const classes = {
  rowIndent: `${PREFIX}-rowIndent`,
  tableRow: `${PREFIX}-tableRow`,
};

const StyledTableRow = styled(TableRow)(() => ({
  [`& .${classes.rowIndent}`]: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      textAlign: "left",
      paddingLeft: 45,
    },
  },

  [`& .${classes.tableRow}`]: {
    "cursor": "pointer",
    "&.MuiTableRow-root:hover, &.MuiTableRow-root:focus-visible": {
      background: "rgba(0, 0, 0, 0.04)",
    },
  },
}));

interface Props {
  appState: Query;
  stateName: string;
}

export default function CountyInstType({ appState, stateName }: Props) {
  const [countyData, setCountyData] = React.useState<KeyValue[]>([]);
  const [instTypeData, setInstTypeData] = React.useState<KeyValue>({
    COMMERCIAL: [],
    SAVINGS: [],
    FOREIGN: [],
  });
  const [previousSearch, setPreviousSearch] = React.useState<KeyValue>({});

  const bankTypes = React.useMemo(
    () => [
      `STNAMEBR:"${stateName}"`,
      `INSURED:CB AND STNAMEBR:"${stateName}"`,
      `INSURED:SA AND STNAMEBR:"${stateName}"`,
      `INSURED:IB AND STNAMEBR:"${stateName}"`,
    ],
    [stateName],
  );

  React.useEffect(() => {
    const filtersSearch = {
      filters: appState.filters,
      search: appState.search,
      sort: appState.sort,
    };
    if (
      appState.filters.bhcRSSD &&
      (Object.keys(appState.ui.marketSelection[0]).length === 0 ||
        appState.filters.stateNames[0] === "ALL") &&
      JSON.stringify(filtersSearch) !== JSON.stringify(previousSearch)
    ) {
      setPreviousSearch(filtersSearch);
      let isMounted = true;

      const fetchUrls = bankTypes.map((val: string) =>
        generateBranchOfficeDepositsApiUrl(
          appState,
          {
            by: "CNTYNAMB",
            limit: 10000,
            sum: ["DEPSUMBR"],
          },
          undefined,
          undefined,
          "CERT",
          val,
        ),
      );

      (async () => {
        await Promise.all(
          fetchUrls.map((url: KeyValue) =>
            fetchData(`${ApiBaseUrl()}/sod`, url),
          ),
        )
          .then((response) => {
            if (isMounted) {
              const commercialStates = response[1].data;
              const savingsStates = response[2].data;
              const foreignStates = response[3].data;

              setCountyData(response[0].data);
              setInstTypeData({
                ...instTypeData,
                COMMERCIAL: filterDataForRow(
                  "CNTYNAMB",
                  commercialStates,
                  response[0].data,
                ),
                SAVINGS: filterDataForRow(
                  "CNTYNAMB",
                  savingsStates,
                  response[0].data,
                ),
                FOREIGN: filterDataForRow(
                  "CNTYNAMB",
                  foreignStates,
                  response[0].data,
                ),
              });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => (isMounted = false));
      })();
    }
  }, [appState, bankTypes, previousSearch, instTypeData]);

  React.useEffect(() => {
    const filtersSearch = {
      filters: appState.filters,
      search: appState.search,
      sort: appState.sort,
      marketSelect: appState.ui.marketSelection,
    };
    if (
      appState.filters.bhcRSSD &&
      Object.keys(appState.ui.marketSelection[0]).length > 0 &&
      appState.filters.stateNames[0] !== "ALL" &&
      JSON.stringify(filtersSearch) !== JSON.stringify(previousSearch)
    ) {
      setPreviousSearch(filtersSearch);
      let isMounted = true;

      const fetchUrls = appState.filters.stateNames
        .filter((state: string) => state)
        .map((state: string, index: number) => {
          return bankTypes.map((val: string) =>
            generateBranchOfficeDepositsApiUrl(
              appState,
              {
                by: "CNTYNAMB",
                limit: 10000,
                sum: ["DEPSUMBR"],
              },
              state,
              appState.ui.marketSelection[index],
              "CERT",
              val,
            ),
          );
        });
      (async () => {
        await Promise.all(
          [].concat
            .apply([], fetchUrls)
            .map((url: KeyValue) => fetchData(`${ApiBaseUrl()}/sod`, url)),
        )
          .then((response) => {
            if (isMounted) {
              const splitResponse = chunkArray(response, 4);

              const allData = combineStateData(
                [].concat.apply(
                  [],
                  splitResponse.map((res: KeyValue) => res[0].data),
                ),
                "CNTYNAMB",
              );

              const commercialData = combineStateData(
                [].concat.apply(
                  [],
                  splitResponse.map((res: KeyValue) => res[1].data),
                ),
                "CNTYNAMB",
              );

              const savingsData = combineStateData(
                [].concat.apply(
                  [],
                  splitResponse.map((res: KeyValue) => res[2].data),
                ),
                "CNTYNAMB",
              );

              const foreignData = combineStateData(
                [].concat.apply(
                  [],
                  splitResponse.map((res: KeyValue) => res[3].data),
                ),
                "CNTYNAMB",
              );
              setCountyData(allData);
              setInstTypeData({
                ...instTypeData,
                COMMERCIAL: filterDataForRow(
                  "CNTYNAMB",
                  commercialData,
                  allData,
                ),
                SAVINGS: filterDataForRow("CNTYNAMB", savingsData, allData),
                FOREIGN: filterDataForRow("CNTYNAMB", foreignData, allData),
              });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => (isMounted = false));
      })();
    }
  }, [appState, bankTypes, previousSearch, instTypeData]);

  const countyRows = countyData.map((data, int) => {
    return data && data.CNTYNAMB ? (
      <React.Fragment key={`${data.CNTYNAMB}_${int}`}>
        <StyledTableRow
          className={classes.tableRow}
          id={`${data.CNTYNAMB}_${int}`}
        >
          <TableCell className={classes.rowIndent}>{data.CNTYNAMB}</TableCell>
          <TableCell />
          <TableCell>
            {instTypeData.COMMERCIAL[int]
              ? instTypeData.COMMERCIAL[int].unique_count
              : 0}
          </TableCell>
          <TableCell>
            {instTypeData.COMMERCIAL[int]
              ? instTypeData.COMMERCIAL[int].count
              : 0}
          </TableCell>
          <TableCell>
            {instTypeData.COMMERCIAL[int] &&
            instTypeData.COMMERCIAL[int].sum_DEPSUMBR
              ? instTypeData.COMMERCIAL[int].sum_DEPSUMBR.toLocaleString()
              : 0}
          </TableCell>
          <TableCell>
            {instTypeData.SAVINGS[int]
              ? instTypeData.SAVINGS[int].unique_count
              : 0}
          </TableCell>
          <TableCell>
            {instTypeData.SAVINGS[int] ? instTypeData.SAVINGS[int].count : 0}
          </TableCell>
          <TableCell>
            {instTypeData.SAVINGS[int] && instTypeData.SAVINGS[int].sum_DEPSUMBR
              ? instTypeData.SAVINGS[int].sum_DEPSUMBR.toLocaleString()
              : 0}
          </TableCell>
          <TableCell>
            {instTypeData.FOREIGN[int]
              ? instTypeData.FOREIGN[int].unique_count
              : 0}
          </TableCell>
          <TableCell>
            {instTypeData.FOREIGN[int] ? instTypeData.FOREIGN[int].count : 0}
          </TableCell>
          <TableCell>
            {instTypeData.FOREIGN[int] && instTypeData.FOREIGN[int].sum_DEPSUMBR
              ? instTypeData.FOREIGN[int].sum_DEPSUMBR.toLocaleString()
              : 0}
          </TableCell>
        </StyledTableRow>
      </React.Fragment>
    ) : (
      <></>
    );
  });

  return countyRows.length > 0 ? <>{countyRows}</> : <></>;
}
